import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import i18n from '@i18next/i18n';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import { safeLoadSLR, substr2 } from 'utils';
import { isLicenseValid } from '@src/js/common/getPrivilegeAttr';
import { showPermitApply } from '@src/js/common/ark-extensions/dc-components/docs-component-permit-apply';
import { SlideDataServices, onSlideAppReady } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import {
    IAuthGuideManager,
    IAuthGuideManagerInjectable,
    IAuthGuideManagerServiceDelegates,
} from './auth-guide-manager.interface';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { IPcEditorExtensionService } from '@src/slide/application/service/edit-extension/core/editor/pc/interface';
import { ApplyResult, IApplyAuthService } from '@src/slide/application/service/apply-auth-service/core/interface';

interface IAuthResponse {
    apply: boolean;
    noPass?: boolean;
    pass?: boolean;
}

export class AuthGuideManager implements IAuthGuideManager {
    private applyAuthStatus: IAuthResponse;
    private extensionService: IPcEditorExtensionService;
    private applyAuthService: IApplyAuthService;
    private hasApplyAuthInit = false;

    public constructor(private injectable: IAuthGuideManagerInjectable) {
        if (this.injectable.shouldListenLoginGuide) {
            eventBus.on(EventKeys.SHOW_LOGIN_GUIDE_PC, () => {
                this.showLoginGuide();
            });
        }

        if (this.injectable.shouldListenAuthGuide) {
            eventBus.on(EventKeys.SHOW_AUTH_GUIDE_PC, () => {
                this.showAuthGuide();
            });
        }
    }

    public setServiceDelegates(services: IAuthGuideManagerServiceDelegates) {
        const { extensionService, applyAuthService } = services;
        this.extensionService = extensionService;
        this.applyAuthService = applyAuthService;

        onSlideAppReady(() => {
            this.setAuthStatus();
        });
    }

    private setAuthStatus() {
        if (this.hasApplyAuthInit) return;
        this.hasApplyAuthInit = true;
        // 拉取 ‘只能查看’ 按钮相关tips
        this.applyAuthService.init().then(() => {
            const { hasApplied, applyResult } = this.applyAuthService.applyInfo;
            this.applyAuthStatus = { apply: hasApplied, pass: applyResult === ApplyResult.Pass,
                noPass: applyResult === ApplyResult.NotPass };
        });
    }

    private getExtensionCallbacks() {
        return this.extensionService.getCallBackAdapter();
    }

    private showLoginGuide = () => {
        if (!this.getExtensionCallbacks()?.isLogin?.()) {
            tdw({
                opername: 'doc_login',
                module: 'readonly',
                action: 'try_edit_toast_exp_pc',
            });
            Snackbar.show({
                message: i18n.t('你只能查看此文档。若要编辑文档，请登录后编辑。'),
                action: i18n.t('立即登录'),
                closable: false,
                duration: 5000,
                type: 'info',
                onClickAction: () => {
                    tdw({
                        opername: 'doc_login',
                        module: 'readonly',
                        action: 'try_edit_toast_login_pc',
                    });
                    this.showLogin();
                },
            });
        }
    };

    private showLogin = () => {
        tdw({
            opername: 'doc_login',
            module: 'readonly',
            action: 'login_exp_pc',
        });

        this.getExtensionCallbacks()?.login?.();
    };

    private showAuthGuide = () => {
        if (this.applyAuthStatus?.apply && !this.applyAuthStatus?.noPass && !this.applyAuthStatus?.pass) {
            Snackbar.show({
                message: i18n.t('权限申请已提交，请等待创建者 {0} 审核。', { 0: substr2(SlideDataServices.DocInfoManager()?.extra.ownerNick || '', 7) }),
                duration: 3000,
                type: 'info',
            });
        } else {
            tdw({
                opername: 'doc_author',
                module: 'edit',
                action: 'try_edit_toast_apply_exp_pc',
            });
            if (!this.injectable.shouldShowNoAuthGuide) {
                return false;
            }
            if (this.applyAuthStatus?.noPass) {
                Snackbar.show({
                    message: i18n.t('申请暂未通过，请在24小时后重试。'),
                    autoClose: true,
                    duration: 3000,
                });
                return;
            }

            if (!this.applyAuthService.canApplyEdit()) {
                const hasLicense = isLicenseValid();
                // 如果不允许查看者申请编辑权限，提示只查看
                Snackbar.show({
                    message: !hasLicense ? i18n.t('编辑失败，企业授权已经到期，请联系管理员更新授权。') : i18n.t('该文档只能查看。'),
                    closable: false,
                    duration: 3000,
                    type: 'info',
                });
                return;
            }
            const action = SlideDataServices.PrivilegeManager()?.canGenerateCopies ? i18n.t('编辑文档') : i18n.t('申请编辑权限');
            // 查看者可以申请编辑权限
            Snackbar.show({
                closable: false,
                duration: 3000,
                type: 'info',
                message: i18n.t('当前文档权限为“只能查看”。'),
                action,
                onClickAction: () => {
                    tdw({
                        opername: 'doc_author',
                        module: 'edit',
                        action: 'try_edit_toast_apply_clk_pc',
                    });
                    this.showAuthApply();
                },
            });
        }
    };

    private showAuthApply = () => {
        showPermitApply({});
        // @ts-ignore
        if (SlideDataServices.PrivilegeManager()?.canGenerateCopies) {
            // 编辑文档
            tdw({
                opername: 'doc_author',
                module: 'edit',
                action: 'clk_tip',
            });
        } else {
            // 申请文档权限
            tdw({
                opername: 'doc_author',
                module: 'edit',
                action: 'readonly_apply_tip_clk_pc',
            });
        }
    };
}
